import { Card, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";

/**
 * Sprint 24 Wave D — PR-D2: shared loading skeleton for admin
 * pages touched in Sprint 24.
 *
 * Standardizes the placeholder shape that the device-approval
 * queue (PR-B3) introduced so loading transitions don't jump and
 * so the operator sees the same visual cue across Members,
 * Invitations, and the Approval Queue. Three placeholder rows
 * each with two skeleton lines.
 *
 * Accessibility: `aria-busy="true"` + `aria-live="polite"` so a
 * screen reader announces the loading state once. The
 * visually-hidden span carries the localized "Loading…" string.
 *
 * No new i18n strings — reuses the existing `common.loading`.
 */
export function LoadingSkeleton({
  rows = 3,
  testId,
}: {
  rows?: number;
  testId?: string;
}): React.ReactNode {
  const { t } = useLocale();
  return (
    <Stack gap={2} aria-busy="true" aria-live="polite" data-testid={testId}>
      <span style={visuallyHidden}>{t("common.loading")}</span>
      {Array.from({ length: rows }).map((_, idx) => (
        <Card key={idx} elevation={1}>
          <Stack gap={2}>
            <span style={skeletonLineStyle(140)} aria-hidden="true" />
            <span style={skeletonLineStyle(220)} aria-hidden="true" />
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

function skeletonLineStyle(maxWidth: number): React.CSSProperties {
  return {
    display: "block",
    blockSize: 12,
    inlineSize: "100%",
    maxInlineSize: maxWidth,
    borderRadius: 6,
    background: "color-mix(in oklab, var(--gt-sys-text) 8%, transparent)",
  };
}

const visuallyHidden: React.CSSProperties = {
  position: "absolute",
  inlineSize: 1,
  blockSize: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
};
